import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthFailure, authService, type AuthService, type AuthStatus } from './auth';
import { pollingInterval } from './polling';
import { recoveryDelay, recoveryMessage, recoveryWait } from './authRecoveryState';
export { recordAuthRecovery, recoveryDelay, recoveryMessage } from './authRecoveryState';
let returnRoute: { identity: string; path: string } | undefined;
export function rememberRecoveryRoute(status: AuthStatus, path: string) {
  if (status.user && /^\/(?!\/)(?:overview|queue|tasks|task-settings|fangtang|fangtang-data|settings|intelligence|blogger-intelligence|projects|leads|replies|account|customers|video-workshop)(?:[/?#]|$)/.test(path))
    returnRoute = {identity: JSON.stringify([status.appId, status.user.id]), path};
}
export function restoredRoute(status: AuthStatus) {
  const old = returnRoute; returnRoute = undefined;
  return old?.identity === JSON.stringify([status.appId, status.user?.id]) ? old.path :
    status.user?.products?.sales === false ? '/video-workshop' : '/overview';
}
export function authPoll(status?: AuthStatus, error?: unknown): number | false {
  const reason = error instanceof AuthFailure ? error.code : error ? 'AUTH_UNAVAILABLE' : status?.reason;
  if (reason === 'AUTH_UNAVAILABLE') return recoveryWait(!error && Number.isFinite(status?.retryAfterSeconds) && status!.retryAfterSeconds! > 0
    ? Math.max(1000, status!.retryAfterSeconds! * 1000) : recoveryDelay());
  if (reason) return false;
  return pollingInterval(10_000) || 60_000;
}
export function useAuthStatus(service: AuthService = authService) {
  const cache = useQueryClient();
  const query = useQuery({queryKey: ['auth-status'], queryFn: service.status, retry: false,
    enabled: service === authService, refetchInterval: q => authPoll(q.state.data, q.state.error),
    refetchIntervalInBackground: true, refetchOnWindowFocus: 'always'});
  const recheck = useCallback(async () => {
    await cache.cancelQueries({queryKey: ['auth-status']});
    try {
      return await cache.fetchQuery({queryKey: ['auth-status'],
        queryFn: () => service.recheck ? service.recheck() : service.status(), retry: false});
    } catch { return undefined; }
  }, [cache, service]);
  const [, tick] = useState(0);
  const reason = query.error instanceof AuthFailure ? query.error.code : query.isError ? 'AUTH_UNAVAILABLE' : query.data?.reason;
  useEffect(() => {
    if (reason !== 'AUTH_UNAVAILABLE') return;
    const timer = window.setInterval(() => tick(value => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [reason]);
  useEffect(() => {
    if (service !== authService) return;
    const wake = () => { void cache.refetchQueries({queryKey: ['auth-status'], type: 'active'}, {cancelRefetch: false}); };
    const recovered = () => {
      const state = cache.getQueryState<AuthStatus>(['auth-status']);
      if (state?.data?.reason === 'AUTH_UNAVAILABLE' || state?.error &&
          (!(state.error instanceof AuthFailure) || state.error.code === 'AUTH_UNAVAILABLE')) void recheck();
    };
    window.addEventListener('online', wake);
    window.addEventListener('account-access-invalid', wake);
    window.addEventListener('account-connection-restored', recovered);
    return () => { window.removeEventListener('online', wake); window.removeEventListener('account-access-invalid', wake);
      window.removeEventListener('account-connection-restored', recovered); };
  }, [cache, service, recheck]);
  return {...query, recheck, recovering: reason === 'AUTH_UNAVAILABLE', recoveryMessage: recoveryMessage()};
}
